import React, { useEffect, useState } from 'react'
import { Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom'
import { ArrowLeft, Grid2X2, LogOut, MoreHorizontal, Pencil, Plus, Trash2, UserRound } from 'lucide-react'
import { configurationError, config } from './config.js'
import { getSupabase } from './auth/supabase.js'
import { getWebCanvasHost } from './web-canvas/host-singleton.js'

function useSession() {
  const [state, setState] = useState(config.e2e ? { loading: false, session: { user: { id: 'e2e-user', email: 'e2e@local.invalid' } } } : { loading: true, session: null })
  useEffect(() => {
    if (config.e2e) return undefined
    const sb = getSupabase()
    if (!sb) { setState({ loading: false, session: null }); return undefined }
    void sb.auth.getSession().then(({ data }) => setState({ loading: false, session: data.session }))
    const { data } = sb.auth.onAuthStateChange((_event, session) => setState({ loading: false, session }))
    return () => data.subscription.unsubscribe()
  }, [])
  return state
}

function Brand() {
  return <div className="brand"><span className="brand-mark"><Grid2X2 size={16} strokeWidth={1.8} /></span><span>Blaze</span></div>
}

function canvasLocation(id) {
  const encodedId = encodeURIComponent(id)
  return String(id).startsWith('cv_') ? `/canvas/${encodedId}#${encodedId}` : `/canvas#${encodedId}`
}

function ConfigurationNotice() {
  return <main className="center-page"><section className="auth-card"><Brand /><h1>Web Canvas 尚未配置</h1><p>{configurationError()}</p><code>复制 .env.example 为 .env.local</code></section></main>
}

function LoginPage({ session }) {
  const navigate = useNavigate()
  const location = useLocation()
  const [mode, setMode] = useState('login')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [message, setMessage] = useState('')
  const [busy, setBusy] = useState(false)
  const sb = getSupabase()

  useEffect(() => {
    if (!session) return
    const next = new URLSearchParams(location.search).get('next')
    navigate(next?.startsWith('/') && !next.startsWith('//') ? next : location.state?.from || '/canvases', { replace: true })
  }, [session, navigate, location.search, location.state])

  async function submit(event) {
    event.preventDefault(); setBusy(true); setMessage('')
    const result = mode === 'signup' ? await sb.auth.signUp({ email, password }) : await sb.auth.signInWithPassword({ email, password })
    setBusy(false)
    if (result.error) setMessage(result.error.message)
    else if (mode === 'signup' && !result.data.session) setMessage('验证邮件已发送，请完成验证后登录。')
  }

  async function google() {
    await sb.auth.signInWithOAuth({ provider: 'google', options: { redirectTo: `${config.appBaseUrl}/canvases` } })
  }

  return <main className="center-page">
    <section className="auth-card">
      <Brand />
      <div className="auth-heading"><h1>{mode === 'signup' ? '创建 Blaze 账户' : '登录 Web Canvas'}</h1><p>你的画布和素材会与桌面端安全同步。</p></div>
      <button className="secondary-btn full" onClick={google}>使用 Google 继续</button>
      <div className="auth-divider"><span>或</span></div>
      <form onSubmit={submit} className="auth-form">
        <label>邮箱<input className="dlg-input" type="email" required autoComplete="email" value={email} onChange={event => setEmail(event.target.value)} /></label>
        <label>密码<input className="dlg-input" type="password" required minLength={8} autoComplete={mode === 'signup' ? 'new-password' : 'current-password'} value={password} onChange={event => setPassword(event.target.value)} /></label>
        {message && <p className="form-message">{message}</p>}
        <button className="primary-btn full" disabled={busy}>{busy ? '请稍候…' : mode === 'signup' ? '注册' : '登录'}</button>
      </form>
      <div className="auth-links">
        <button onClick={() => setMode(mode === 'signup' ? 'login' : 'signup')}>{mode === 'signup' ? '已有账户？登录' : '没有账户？注册'}</button>
        {mode === 'login' && <button onClick={() => navigate('/reset-password')}>忘记密码</button>}
      </div>
    </section>
  </main>
}

function ResetPasswordPage() {
  const [email, setEmail] = useState('')
  const [message, setMessage] = useState('')
  const [recovery, setRecovery] = useState(false)
  const [password, setPassword] = useState('')
  const navigate = useNavigate()
  useEffect(() => {
    const sb = getSupabase()
    void sb.auth.getSession().then(({ data }) => setRecovery(Boolean(data.session && location.hash)))
    const { data } = sb.auth.onAuthStateChange(event => { if (event === 'PASSWORD_RECOVERY') setRecovery(true) })
    return () => data.subscription.unsubscribe()
  }, [])
  async function submit(event) {
    event.preventDefault()
    const sb = getSupabase()
    if (recovery) {
      const { error } = await sb.auth.updateUser({ password })
      setMessage(error ? error.message : '密码已更新，现在可以返回画布。')
      return
    }
    const { error } = await sb.auth.resetPasswordForEmail(email, { redirectTo: `${config.appBaseUrl}/reset-password` })
    setMessage(error ? error.message : '重置邮件已发送。')
  }
  return <main className="center-page"><section className="auth-card">
    <button className="back-link" onClick={() => navigate('/login')}><ArrowLeft size={15} />返回登录</button>
    <h1>{recovery ? '设置新密码' : '重置密码'}</h1><p>{recovery ? '请输入至少 8 位的新密码。' : '输入注册邮箱，我们会发送安全重置链接。'}</p>
    <form onSubmit={submit} className="auth-form">{recovery
      ? <label>新密码<input className="dlg-input" type="password" minLength={8} required value={password} onChange={event => setPassword(event.target.value)} /></label>
      : <label>邮箱<input className="dlg-input" type="email" required value={email} onChange={event => setEmail(event.target.value)} /></label>}<button className="primary-btn full">{recovery ? '更新密码' : '发送重置邮件'}</button></form>
    {message && <p className="form-message">{message}</p>}
  </section></main>
}

const VIDEO_PREVIEW_EXTENSIONS = new Set(['mp4', 'webm'])
const IMAGE_PREVIEW_EXTENSIONS = new Set(['png', 'jpg', 'jpeg', 'webp', 'svg', 'avif'])

function toTimestamp(value) {
  const numeric = Number(value)
  if (Number.isFinite(numeric) && numeric > 0) return numeric
  const parsed = value ? new Date(value).getTime() : NaN
  return Number.isFinite(parsed) ? parsed : 0
}

function formatRelativeTime(value) {
  const ms = toTimestamp(value)
  if (!ms) return ''
  const mins = Math.floor((Date.now() - ms) / 60000)
  if (mins < 1) return '刚刚'
  if (mins < 60) return `${mins} 分钟前`
  const hours = Math.floor(mins / 60)
  if (hours < 24) return `${hours} 小时前`
  const days = Math.floor(hours / 24)
  if (days === 1) return '昨天'
  if (days < 7) return `${days} 天前`
  if (days < 14) return '上周'
  const date = new Date(ms)
  return `${date.getMonth() + 1} 月 ${date.getDate()} 日`
}

// Hold the last successfully shown pixels through URL changes (presign
// rotation, blob swap after mirroring, variant upgrade): the <img>/backdrop
// only moves to a new URL once it has finished loading off-screen, so cards
// never blank-flash mid-refresh.
function useSettledImageUrl(url, onProbeError) {
  const [settled, setSettled] = useState(url || '')
  useEffect(() => {
    if (!url || url === settled) return undefined
    if (!settled) { setSettled(url); return undefined }
    let alive = true
    const probe = new Image()
    probe.onload = () => { if (alive) setSettled(url) }
    probe.onerror = () => { if (alive) onProbeError?.() }
    probe.src = url
    return () => { alive = false }
  }, [url])
  return url ? settled : ''
}

function CanvasPreviewAsset({ asset, name }) {
  // No crossOrigin here: the list never draws previews to a <canvas>, and CORS
  // mode turns presigned R2 originals (no ACAO header by design) into blank
  // cells. Step 0 = preferred URL, step 1 = one retry on the original.
  const [step, setStep] = useState(0)
  useEffect(() => { setStep(0) }, [asset.url])
  const fallback = asset.originalUrl && asset.originalUrl !== asset.url ? asset.originalUrl : null
  const src = step === 1 && fallback ? fallback : asset.url
  const failed = step > (fallback ? 1 : 0)
  const ext = String(asset.ext || '').toLowerCase()
  const mime = String(asset.mime || '').toLowerCase()
  const isVideo = mime.startsWith('video/') || VIDEO_PREVIEW_EXTENSIONS.has(ext)
  const settledSrc = useSettledImageUrl(isVideo ? '' : src, () => setStep(current => current + 1))
  const isImage = mime.startsWith('image/') || IMAGE_PREVIEW_EXTENSIONS.has(ext) || (!mime && !ext)
  if (failed || (!isVideo && !isImage)) return null
  if (isVideo) {
    return <video
      src={src}
      aria-label={`${name} video preview`}
      muted
      playsInline
      preload="metadata"
      onLoadedMetadata={event => {
        const video = event.currentTarget
        if (Number.isFinite(video.duration) && video.duration > 0.05) video.currentTime = Math.min(0.1, video.duration / 2)
      }}
      onError={() => setStep(current => current + 1)}
    />
  }
  return <img src={settledSrc || src} alt="" decoding="async" draggable="false" onError={() => setStep(current => current + 1)} />
}

function CanvasPreview({ canvas }) {
  const assets = canvas.previewAssets || []
  return <div className="canvas-card-gallery">
    <div className="canvas-card-cell canvas-card-cell--hero">{assets[0] ? <CanvasPreviewAsset key={assets[0].hash} asset={assets[0]} name={canvas.name} /> : null}</div>
    {[1, 2, 3, 4].map(index => <div key={index} className="canvas-card-cell">{assets[index] ? <CanvasPreviewAsset key={assets[index].hash} asset={assets[index]} name={canvas.name} /> : null}</div>)}
  </div>
}

function CanvasCard({ canvas, menuOpen, onToggleMenu, onRename, onDelete }) {
  const assets = canvas.previewAssets || []
  const hero = useSettledImageUrl(assets[0]?.url || '')
  const time = formatRelativeTime(canvas.updatedAt)
  const count = Number(canvas.itemCount) || 0
  const meta = [count ? `${count} 项` : assets.length ? '' : '空画布', time].filter(Boolean).join(' · ')
  const cardStyle = hero ? { '--card-hero': `url("${hero.replace(/"/g, '\\"')}")` } : undefined
  return <article className={`canvas-card${menuOpen ? ' is-menu-open' : ''}`} style={cardStyle} onClick={() => { location.href = canvasLocation(canvas.id) }}>
    {hero ? <div className="canvas-card-backdrop"><div className="canvas-card-hero" /><div className="canvas-card-wash" /></div> : null}
    <button className="canvas-card-more" aria-label="画布菜单" onClick={event => { event.stopPropagation(); onToggleMenu(canvas.id) }}><MoreHorizontal size={14} strokeWidth={1.9} /></button>
    {menuOpen && <div className="card-menu" onClick={event => event.stopPropagation()}>
      <button onClick={() => onRename(canvas)}><Pencil size={14} />重命名</button>
      <button className="danger" onClick={() => onDelete(canvas)}><Trash2 size={14} />删除</button>
    </div>}
    <div className="canvas-card-info">
      <div className="canvas-card-name">{canvas.name}</div>
      <div className="canvas-card-meta">{meta}</div>
    </div>
    {assets.length ? <CanvasPreview canvas={canvas} /> : <div className="canvas-card-blank" />}
  </article>
}

// Stale-while-revalidate seed for the list page: only plain summaries (no
// resolved URLs — presigns expire) so a revisit paints cards instantly while
// the real list() revalidates in the background.
const LIST_CACHE_KEY = 'blaze.web.canvas-list-cache'

function readCachedSummaries() {
  try {
    const rows = JSON.parse(localStorage.getItem(LIST_CACHE_KEY) || 'null')
    return Array.isArray(rows) && rows.length ? rows : null
  } catch { return null }
}

function writeCachedSummaries(summaries) {
  try {
    localStorage.setItem(LIST_CACHE_KEY, JSON.stringify(summaries.map(({ id, name, itemCount, previews, createdAt, updatedAt }) => ({ id, name, itemCount, previews, createdAt, updatedAt }))))
  } catch { /* quota/unavailable — the next successful list() retries */ }
}

function CanvasesPage({ session }) {
  const host = getWebCanvasHost()
  const navigate = useNavigate()
  const [canvases, setCanvases] = useState(() => readCachedSummaries() || [])
  const [loading, setLoading] = useState(canvases.length === 0)
  const [error, setError] = useState('')
  const [account, setAccount] = useState(false)
  const [view, setView] = useState('all')
  const [menuId, setMenuId] = useState(null)

  async function refresh(options = {}) {
    if (!options.background) setLoading(true)
    try {
      // Two-phase: cards paint the moment the index answers; thumbnails
      // hydrate right after. On a background refresh, cards keep their
      // already-loaded previews until the fresh ones land (no blank flash).
      const summaries = await host.canvases.list({ previews: false })
      setCanvases(current => {
        const previousById = new Map(current.map(item => [item.id, item]))
        return summaries.map(summary => ({ ...summary, previewAssets: previousById.get(summary.id)?.previewAssets || [] }))
      })
      writeCachedSummaries(summaries)
      setError('')
      if (!options.background) setLoading(false)
      const hydrated = await host.canvases.attachPreviews(summaries)
      // Merge into current state instead of replacing it: an optimistic
      // delete/rename that happened between the two phases must not be
      // resurrected by the hydration snapshot. Per-hash the first shown
      // preview sticks for the session — a later hydration for the SAME hash
      // (variant/poster finished deriving → background refresh) returns a
      // different URL or even flips a <video> cell to a poster <img>, and
      // swapping an already-displayed cell reads as a flash. Content is
      // hash-addressed, so freezing the URL is always correct; the next
      // visit starts on the derived variant directly.
      setCanvases(current => {
        const hydratedById = new Map(hydrated.map(item => [item.id, item]))
        return current.map(item => {
          const match = hydratedById.get(item.id)
          if (!match) return item
          const shownByHash = new Map((item.previewAssets || []).map(asset => [asset.hash, asset]))
          const previewAssets = (match.previewAssets || []).map(asset => shownByHash.get(asset.hash) || asset)
          return { ...item, previews: match.previews, previewAssets }
        })
      })
    } catch (reason) { setError(reason.message || '画布加载失败') }
    finally { if (!options.background) setLoading(false) }
  }
  useEffect(() => {
    // Documents whose asset uploads finished while no canvas page was open
    // still need their push (which refreshes the canvas_index cover/count).
    void host.canvases.resumePending?.()
    void refresh({ background: canvases.length > 0 })
  }, [])
  useEffect(() => {
    const assetMap = host._assetMap
    if (!assetMap?.addEventListener) return undefined
    let timer = 0
    const updatePreviews = () => {
      clearTimeout(timer)
      timer = setTimeout(() => { void refresh({ background: true }) }, 750)
    }
    assetMap.addEventListener('asset-variant-ready', updatePreviews)
    assetMap.addEventListener('poster-ready', updatePreviews)
    return () => {
      clearTimeout(timer)
      assetMap.removeEventListener('asset-variant-ready', updatePreviews)
      assetMap.removeEventListener('poster-ready', updatePreviews)
    }
  }, [])

  useEffect(() => {
    if (!menuId) return undefined
    const close = () => setMenuId(null)
    const onKey = event => { if (event.key === 'Escape') setMenuId(null) }
    document.addEventListener('click', close)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('click', close)
      document.removeEventListener('keydown', onKey)
    }
  }, [menuId])

  async function create() { const id = await host.canvases.create('Untitled'); location.href = canvasLocation(id) }
  // Optimistic: the card updates immediately; the host call runs in the
  // background and the card is restored (with an alert) only on failure.
  function rename(canvas) {
    const name = prompt('画布名称', canvas.name)?.trim()
    if (!name || name === canvas.name) return
    setCanvases(current => current.map(item => (item.id === canvas.id ? { ...item, name } : item)))
    void host.canvases.rename(canvas.id, name).catch(() => {
      setCanvases(current => current.map(item => (item.id === canvas.id ? { ...item, name: canvas.name } : item)))
      window.alert('重命名失败，请重试')
    })
  }
  function remove(canvas) {
    if (!confirm(`删除“${canvas.name}”？`)) return
    setCanvases(current => current.filter(item => item.id !== canvas.id))
    void host.canvases.delete(canvas.id).catch(() => {
      setCanvases(current => (current.some(item => item.id === canvas.id) ? current : [...current, canvas]))
      window.alert('删除失败，请重试')
    })
  }
  async function signOut() { await getSupabase()?.auth.signOut(); navigate('/login') }
  const toggleMenu = id => setMenuId(current => (current === id ? null : id))
  const renameFromMenu = canvas => { setMenuId(null); rename(canvas) }
  const removeFromMenu = canvas => { setMenuId(null); remove(canvas) }
  // 全部 sorts by createdAt so autosave (updatedAt) never reshuffles the grid.
  const displayList = view === 'recent'
    ? [...canvases].sort((a, b) => toTimestamp(b.updatedAt) - toTimestamp(a.updatedAt)).slice(0, 10)
    : [...canvases].sort((a, b) => toTimestamp(b.createdAt) - toTimestamp(a.createdAt))

  return <main className="projects-page">
    <header className="projects-header"><Brand /><div className="header-actions"><button className="primary-btn" onClick={create}><Plus size={16} />新建画布</button><button className="avatar-btn" onClick={() => setAccount(!account)}>{session.user.email?.slice(0, 2).toUpperCase() || <UserRound size={16} />}</button></div>
      {account && <div className="account-menu"><p>{session.user.email}</p><button onClick={signOut}><LogOut size={14} />退出登录</button></div>}
    </header>
    <section className="projects-content">
      {error && <div className="error-banner">{error}</div>}
      {loading ? <div className="empty-state">正在加载…</div> : <>
        <div className="cv-toolbar">
          <div className="cv-seg" role="tablist">
            <button type="button" role="tab" aria-selected={view === 'all'} onClick={() => setView('all')}>全部</button>
            <button type="button" role="tab" aria-selected={view === 'recent'} onClick={() => setView('recent')}>最近</button>
          </div>
          <span className="cv-count">{view === 'recent' ? '最近编辑的 10 张' : `${canvases.length} 张 · 按最近更新排序`}</span>
        </div>
        {displayList.length
          ? <div className="canvas-grid">
              <button type="button" className="canvas-new-tile" onClick={create}><span className="ico"><Plus size={17} /></span><span className="label">新建画布</span></button>
              {displayList.map(canvas => <CanvasCard key={canvas.id} canvas={canvas} menuOpen={menuId === canvas.id} onToggleMenu={toggleMenu} onRename={renameFromMenu} onDelete={removeFromMenu} />)}
            </div>
          : <div className="cv-empty">
              <div className="cv-empty-card">
                <span className="ico"><Grid2X2 size={28} strokeWidth={1.4} /></span>
                <h2>创建第一张画布</h2>
                <p>浏览器中的修改会先保存到本机，再安全同步到云端。</p>
                <button className="primary-btn" onClick={create}><Plus size={16} />新建画布</button>
              </div>
            </div>}
      </>}
    </section>
  </main>
}

function Guard({ state, children }) {
  const location = useLocation()
  if (state.loading) return <main className="center-page">正在加载…</main>
  if (!state.session) return <Navigate to="/login" state={{ from: location.pathname }} replace />
  return children
}

export function App() {
  const auth = useSession()
  if (configurationError()) return <ConfigurationNotice />
  if (!config.enabled) return <main className="center-page">Web Canvas 暂未开放。</main>
  return <Routes>
    <Route path="/login" element={<LoginPage session={auth.session} />} />
    <Route path="/reset-password" element={<ResetPasswordPage />} />
    <Route path="/canvases" element={<Guard state={auth}><CanvasesPage session={auth.session} /></Guard>} />
    <Route path="*" element={<Navigate to={auth.session ? '/canvases' : '/login'} replace />} />
  </Routes>
}
